import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { distanceKmFromUser } from '../components/guest-map/distance';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fetches the public venue events stream every 90 s, filters them down to
// venues within `radiusKm` of the user, groups by venue, and exposes a
// `venueEventState(venueId)` helper that returns 'live' | 'starting' | null
// based on a 30 s clock tick.
export function useNearbyEvents({ userPosition, radiusKm }) {
  const [events, setEvents] = useState([]);
  const [nowTick, setNowTick] = useState(() => Date.now());

  useEffect(() => {
    let cancelled = false;
    const fetchEvents = async () => {
      try {
        const res = await axios.get(`${API}/public/venue-events`);
        if (cancelled) return;
        setEvents(res.data?.events || []);
      } catch {
        if (!cancelled) setEvents([]);
      }
    };
    fetchEvents();
    const interval = setInterval(fetchEvents, 90_000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);

  const nearbyEvents = useMemo(() => {
    if (!userPosition) return events;
    return events.filter((ev) => {
      const v = ev?.venue;
      if (!v || typeof v.latitude !== 'number' || typeof v.longitude !== 'number') return false;
      return distanceKmFromUser(v, userPosition) <= radiusKm;
    });
  }, [events, userPosition, radiusKm]);

  const eventsByVenueId = useMemo(() => {
    const map = new Map();
    for (const ev of nearbyEvents) {
      const vid = ev?.venue?.id;
      if (!vid) continue;
      if (!map.has(vid)) map.set(vid, []);
      map.get(vid).push(ev);
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => String(a.start_at_utc).localeCompare(String(b.start_at_utc)));
    }
    return map;
  }, [nearbyEvents]);

  const venueEventState = useCallback((venueId) => {
    const arr = eventsByVenueId.get(venueId);
    if (!arr || !arr.length) return null;
    const now = nowTick;
    for (const ev of arr) {
      const start = new Date(ev.start_at_utc).getTime();
      const end = new Date(ev.end_at_utc).getTime();
      if (now >= start && now < end - 30 * 60 * 1000) return 'live';
      if (now < start && start - now <= 30 * 60 * 1000) return 'starting';
    }
    return null;
  }, [eventsByVenueId, nowTick]);

  return { nearbyEvents, eventsByVenueId, venueEventState };
}
